import { TLShapeId, useEditor, useValue } from "@tldraw/tldraw";
import React from "react";
import { SECTIONS } from "pages/flyer/builder/constants";
import { DropdownChevronIcon } from "pages/flyer/builder/drawer/components/Primitives/icons/DropdownChevronIcon";
import cn from "classnames";
import { ShapeList } from "./ShapeListComponent";
import { EyeOpenIcon16, EyeClosedIcon16, LockUnlockedIcon, LockLockedIcon } from "./icons";
import "./style.css";
import { pairArtWorkLines } from "../../../../../../utils";
import { getStoredOrientation, extractIDsFromURL } from "pages/flyer/builder/utils/orientationStorage";
import { DocumentOrientation } from "pages/flyer/builder/drawer/types";

const PortraitIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M6 6C6 5.46957 6.21071 4.96086 6.58579 4.58579C6.96086 4.21071 7.46957 4 8 4H16C16.5304 4 17.0391 4.21071 17.4142 4.58579C17.7893 4.96086 18 5.46957 18 6V18C18 18.5304 17.7893 19.0391 17.4142 19.4142C17.0391 19.7893 16.5304 20 16 20H8C7.46957 20 6.96086 19.7893 6.58579 19.4142C6.21071 19.0391 6 18.5304 6 18V6Z"
      stroke="#2E3860"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10.1846 14.2708L11.9997 16.0859M11.9997 16.0859L13.8149 14.2708M11.9997 16.0859V7.91776M10.1846 9.73291L11.9997 7.91776M11.9997 7.91776L13.8149 9.73291"
      stroke="#2E3860"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const LandscapeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M4 8C4 7.46957 4.21071 6.96086 4.58579 6.58579C4.96086 6.21071 5.46957 6 6 6H18C18.5304 6 19.0391 6.21071 19.4142 6.58579C19.7893 6.96086 20 7.46957 20 8V16C20 16.5304 19.7893 17.0391 19.4142 17.4142C19.0391 17.7893 18.5304 18 18 18H6C5.46957 18 4.96086 17.7893 4.58579 17.4142C4.21071 17.0391 4 16.5304 4 16V8Z"
      stroke="#2E3860"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M9.73117 10.1836L7.91602 11.9987M7.91602 11.9987L9.73117 13.8139M7.91602 11.9987H16.0842M14.269 10.1836L16.0842 11.9987M16.0842 11.9987L14.269 13.8139"
      stroke="#2E3860"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// Above this count we bail out of rendering the shape list. Each
// `<ShapeItem>` in the panel registers ~6 reactive `useValue` subscriptions
// (shape, children, hidden, selected, name, hovered) + a `useDraggable` /
// `useDroppable` pair from @dnd-kit. Mounting 1000+ synchronously locks the
// browser. 200 leaves headroom for typical dense flyer designs without
// crossing into "panel hangs on open" territory.
const LAYERS_PANEL_RENDER_THRESHOLD = 200;

const NodesList = () => {
  const [hiddenPairs, setHiddenPairs] = React.useState<{ [key: string]: boolean }>({});
  const [isArtWorkLinesHidden, setIsArtWorkLinesHidden] = React.useState(true);

  // Add state for section visibility
  const [sectionVisibility, setSectionVisibility] = React.useState({
    front: true,
    back: true,
    outside: true,
  });

  const editor = useEditor();

  // Get current orientation for Front/Back icons
  const { designId, campaignId } = extractIDsFromURL();
  const storedOrientation = getStoredOrientation(designId, campaignId);
  const isPortrait = storedOrientation === DocumentOrientation.Portrait;
  const OrientationIcon = isPortrait ? PortraitIcon : LandscapeIcon;
  const shapeIds = useValue("shapeIds", () => editor.getSortedChildIdsForParent(editor.getCurrentPageId()), [editor]);

  // All infrastructure shape IDs that should be hidden from the layers panel.
  // These include canvas backgrounds (always at bottom) and shapes that
  // bringDieLinesToFront repositions to the top of the z-stack.
  const INFRASTRUCTURE_SHAPE_IDS = new Set<string>([
    // Canvas backgrounds
    "shape:front",
    "shape:back",
    "shape:frontBlackLine",
    "shape:backBlackLine",
    // Safety lines (dash border lines)
    "shape:frontBorderDashLine",
    "shape:backBorderDashLine",
    // Cut lines (red lines)
    "shape:frontRedLine",
    "shape:backRedLine",
    "shape:frontFoldLine",
    "shape:backFoldLine",
    // Labels
    "shape:frontLabel",
    "shape:backLabel",
    // Die cut lines
    "shape:diecutLineFront",
    "shape:diecutLineBack",
    // Cut lines
    "shape:cutLineFront",
    "shape:cutLineBack",
    // A6 Safety lines
    "shape:a6SafetyLineFront",
    "shape:a6SafetyLineBack",
    // Tearaway lines
    "shape:TearawayCutLineFront",
    "shape:TearawayCutLineBack",
    "shape:tearawayFlyerBuilderCutlineFront",
    "shape:tearawayFlyerBuilderCutlineBack",
    "shape:tearawaySafetyLineFront",
    "shape:tearawaySafetyLineBack",
    // CustomMeasure
    "shape:customMeasure",
  ]);

  const filteredShapeIds = Array.from(new Set(shapeIds.filter((id) => !INFRASTRUCTURE_SHAPE_IDS.has(id as string))));

  // Categorize shapes into sections
  const categorizeShapes = () => {
    const frontShapes: TLShapeId[] = [];
    const backShapes: TLShapeId[] = [];
    const outsideShapes: TLShapeId[] = [];

    // Skip the O(N) categorization + 2 `getShapePageBounds` per shape when
    // we're above the render threshold — the panel will show the bail-out
    // message anyway, so this work would be pure cost.
    if (filteredShapeIds.length > LAYERS_PANEL_RENDER_THRESHOLD) {
      return { frontShapes, backShapes, outsideShapes };
    }

    // Get front and back canvas shapes
    const frontCanvas = editor.getShape("shape:front" as TLShapeId);
    const backCanvas = editor.getShape("shape:back" as TLShapeId);

    if (!frontCanvas || !backCanvas) {
      // Fallback: if canvas shapes don't exist, put all shapes in outside
      return { frontShapes: [], backShapes: [], outsideShapes: filteredShapeIds };
    }

    // Helper function to check if two shapes overlap using page bounds.
    // getShapePageBounds works for ALL shape types including arrows and lines
    // which don't have props.w/h.
    const shapesOverlap = (shape1: any, shape2: any) => {
      if (!shape1 || !shape2) return false;

      const pb1 = editor.getShapePageBounds(shape1.id);
      const pb2 = editor.getShapePageBounds(shape2.id);
      if (!pb1 || !pb2) return false;

      return pb1.x < pb2.x + pb2.w && pb1.x + pb1.w > pb2.x && pb1.y < pb2.y + pb2.h && pb1.y + pb1.h > pb2.y;
    };

    filteredShapeIds.forEach((shapeId) => {
      const shape = editor.getShape(shapeId);
      if (!shape) return;

      // NEW: Check if the shape has a forced categorization
      const forcedCategory = (shape.props as any)?.forcedLayerCategory;

      // Check if shape overlaps with front or back canvas
      const overlapsFront = shapesOverlap(shape, frontCanvas);
      const overlapsBack = shapesOverlap(shape, backCanvas);

      if (forcedCategory === "front" || forcedCategory === "Front") {
        // Force categorization to Front, BUT only if it is over the front canvas
        if (overlapsFront) {
          frontShapes.push(shapeId);
        } else {
          // If forced to Front but not over the front canvas, send to Outside Frames
          outsideShapes.push(shapeId);
        }
      } else if (forcedCategory === "back" || forcedCategory === "Back") {
        // Force categorization to Back, BUT only if it is over the back canvas
        if (overlapsBack) {
          backShapes.push(shapeId);
        } else {
          // If forced to Back but not over the back canvas, send to Outside Frames
          outsideShapes.push(shapeId);
        }
      } else {
        // Normal auto-detection (original behavior)
        if (overlapsFront && overlapsBack) {
          // If shape overlaps both, prioritize front
          frontShapes.push(shapeId);
        } else if (overlapsFront) {
          frontShapes.push(shapeId);
        } else if (overlapsBack) {
          backShapes.push(shapeId);
        } else {
          // Shapes that don't overlap with either canvas are considered "outside frames"
          outsideShapes.push(shapeId);
        }
      }
    });

    return { frontShapes, backShapes, outsideShapes };
  };

  const { frontShapes, backShapes, outsideShapes } = categorizeShapes();

  React.useEffect(() => {
    pairArtWorkLines.forEach(({ front, back, name }) => {
      const frontShape = editor.getShape(front as TLShapeId);
      const backShape = editor.getShape(back as TLShapeId);
      if (frontShape && backShape) {
        const isHidden = frontShape.meta.hidden && backShape.meta.hidden;
        // @ts-ignore
        setHiddenPairs((prev) => ({ ...prev, [name]: isHidden }));
      }
    });
  }, [editor]);

  const handleArtWorkLines = (event: React.MouseEvent, frontId: TLShapeId, backId: TLShapeId, pairName: string) => {
    setHiddenPairs((prev) => ({ ...prev, [pairName]: !prev[pairName] }));
    event.stopPropagation();

    [frontId, backId].forEach((id) => {
      const shape = editor.getShape(id);
      if (shape) {
        editor.run(
          () => {
            editor.updateShape({ ...shape, meta: { hidden: !shape.meta.hidden } });
          },
          { ignoreShapeLock: true }
        );
      }
    });
  };

  const handleHiddenToggle = (event: React.MouseEvent) => {
    setIsArtWorkLinesHidden(!isArtWorkLinesHidden);
    event.stopPropagation();
  };

  const handleToggleAllArtWorkLines = (event: React.MouseEvent) => {
    const allHidden = Object.values(hiddenPairs).every((hidden) => hidden);
    setHiddenPairs((prev) => {
      const newHiddenPairs = { ...prev };
      pairArtWorkLines.forEach(({ name }) => {
        newHiddenPairs[name] = !allHidden;
      });
      return newHiddenPairs;
    });
    event.stopPropagation();

    pairArtWorkLines.forEach(({ front, back }) => {
      [front, back].forEach((id) => {
        const shape = editor.getShape(id as TLShapeId);
        if (shape) {
          editor.run(
            () => {
              editor.updateShape({ ...shape, meta: { hidden: !allHidden } });
            },
            { ignoreShapeLock: true }
          );
        }
      });
    });
  };

  const handleSectionToggle = (section: "front" | "back" | "outside") => {
    setSectionVisibility((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleToggleSectionShapesVisibility = (event: React.MouseEvent, sectionShapeIds: TLShapeId[]) => {
    event.stopPropagation();
    const allHidden = sectionShapeIds.every((id) => {
      const shape = editor.getShape(id);
      return shape?.meta?.hidden;
    });
    sectionShapeIds.forEach((id) => {
      const shape = editor.getShape(id);
      if (shape) {
        editor.run(
          () => {
            editor.updateShape({ ...shape, meta: { ...shape.meta, hidden: !allHidden } });
          },
          { ignoreShapeLock: true }
        );
      }
    });
  };

  const isSectionAllHidden = (sectionShapeIds: TLShapeId[]) => {
    return (
      sectionShapeIds.length > 0 &&
      sectionShapeIds.every((id) => {
        const shape = editor.getShape(id);
        return shape?.meta?.hidden;
      })
    );
  };

  const handleToggleSectionLock = (event: React.MouseEvent, sectionShapeIds: TLShapeId[]) => {
    event.stopPropagation();
    const allLocked = sectionShapeIds.every((id) => {
      const shape = editor.getShape(id);
      return shape?.isLocked;
    });
    sectionShapeIds.forEach((id) => {
      const shape = editor.getShape(id);
      if (shape) {
        editor.run(
          () => {
            editor.updateShape({ ...shape, isLocked: !allLocked });
          },
          { ignoreShapeLock: true }
        );
      }
    });
  };

  const isSectionAllLocked = (sectionShapeIds: TLShapeId[]) => {
    return (
      sectionShapeIds.length > 0 &&
      sectionShapeIds.every((id) => {
        const shape = editor.getShape(id);
        return shape?.isLocked;
      })
    );
  };

  const shapes = useValue(
    "shapes",
    () =>
      filteredShapeIds.map((shapeId) => {
        const shape = editor.getShape(shapeId);
        return shape;
      }),
    [editor, filteredShapeIds]
  );

  // const groups = shapes.filter((shape) => shape?.type === "group");
  // const nonGroupShapes = shapes.filter((shape) => shape?.type !== "group");

  // Close any stuck Radix context menu when clicking outside the layers panel
  // (e.g. clicking on the canvas). Radix doesn't detect outside-clicks swallowed by tldraw.
  const layerPanelRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const handleGlobalPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      // If click is inside the layers panel, ignore
      if (layerPanelRef.current?.contains(target)) return;
      // If click is inside ANY Radix context menu content (portal), ignore.
      // Check multiple selectors for compatibility with old Radix versions.
      if (target.closest("[data-radix-menu-content], [data-radix-collection-item], [role='menu'], [role='menuitem']"))
        return;
      // Click was outside — dismiss any open Radix context menu
      document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
    };
    document.addEventListener("pointerdown", handleGlobalPointerDown, true);
    return () => {
      document.removeEventListener("pointerdown", handleGlobalPointerDown, true);
    };
  }, []);

  return (
    <>
      <div
        ref={layerPanelRef}
        className={cn("layer-panel transition-all duration-200 w-full")}
        data-tour="layers-panel"
      >
        <h2 className="text-lg font-bold mb-6" data-tour="layers-title">
          Layers
        </h2>
        <div>
          <div className="group/artwork w-full flex items-center justify-between">
            <button onClick={(event) => handleHiddenToggle(event)} className="flex items-center cursor-pointer gap-2">
              <div>
                <DropdownChevronIcon
                  style={{
                    transform: !isArtWorkLinesHidden ? "rotate(0deg)" : "rotate(-90deg)",
                    transition: "transform 0.2s ease",
                  }}
                />
              </div>
              <p
                style={{
                  color: "#0C172C",
                  fontFeatureSettings: "'liga' off, 'clig' off",
                  fontFamily: "Inter",
                  fontSize: 14,
                  fontWeight: 400,
                  lineHeight: "16px",
                  margin: 0,
                }}
              >
                Artwork lines
              </p>
            </button>
            <button
              className="shape-visibility-toggle invisible group-hover/artwork:visible"
              onClick={(event) => handleToggleAllArtWorkLines(event)}
            >
              {Object.values(hiddenPairs).every((hidden) => hidden) ? <EyeClosedIcon16 /> : <EyeOpenIcon16 />}
            </button>
          </div>
          {!isArtWorkLinesHidden && (
            <ul
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                listStyle: "none",
                margin: 0,
                padding: 0,
                marginTop: 8,
              }}
            >
              {pairArtWorkLines.map(({ front, back, name }) => (
                <li key={front} className="ml-2">
                  <div className="group/line flex items-center justify-between">
                    <p
                      className="shape-name"
                      style={{
                        color: "#0C172C",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: "Inter",
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "16px",
                        margin: 0,
                        marginLeft: 8,
                      }}
                    >
                      {name}
                    </p>
                    <button
                      onClick={(event) => handleArtWorkLines(event, front as TLShapeId, back as TLShapeId, name)}
                      className="shape-visibility-toggle invisible group-hover/line:visible"
                    >
                      {hiddenPairs[name] ? <EyeClosedIcon16 /> : <EyeOpenIcon16 />}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Bulk-shape bail-out. Each `<ShapeItem>` inside the Front / Back /
            Outside sections registers ~6 reactive `useValue` subscriptions
            (shape record, children, hidden, selected, name, hovered) plus a
            `useDraggable`/`useDroppable` pair from @dnd-kit. Mounting 1000+
            of these synchronously hangs the browser. Above the threshold we
            render a short message instead. Threshold tuned to be well below
            the noticeable-mount-lag point on a typical machine. */}
        {filteredShapeIds.length > LAYERS_PANEL_RENDER_THRESHOLD ? (
          <div className="text-xs text-[#57578E] py-4">
            Layers panel is disabled with {LAYERS_PANEL_RENDER_THRESHOLD}+ shapes for performance. Interact with shapes
            directly on the canvas.
          </div>
        ) : (
          <>
            {/* Front Section */}
            {frontShapes.length > 0 && (
              <div className="mt-4">
                <div className="group/front w-full flex items-center justify-between">
                  <button
                    onClick={() => handleSectionToggle("front")}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <DropdownChevronIcon
                      style={{
                        transform: sectionVisibility.front ? "rotate(0deg)" : "rotate(-90deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                    <OrientationIcon />
                    <p
                      style={{
                        color: "#0C172C",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: "Inter",
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "16px",
                        margin: 0,
                      }}
                    >
                      Front
                    </p>
                  </button>
                  <div className="flex items-center invisible group-hover/front:visible">
                    <button
                      className="shape-visibility-toggle"
                      onClick={(event) => handleToggleSectionLock(event, frontShapes)}
                    >
                      {isSectionAllLocked(frontShapes) ? <LockLockedIcon /> : <LockUnlockedIcon />}
                    </button>
                    <button
                      className="shape-visibility-toggle"
                      onClick={(event) => handleToggleSectionShapesVisibility(event, frontShapes)}
                    >
                      {isSectionAllHidden(frontShapes) ? <EyeClosedIcon16 /> : <EyeOpenIcon16 />}
                    </button>
                  </div>
                </div>
                {sectionVisibility.front && (
                  <div style={{ marginTop: 8 }}>
                    <ShapeList
                      shapeIds={frontShapes}
                      depth={0}
                      shapes={shapes.filter((shape) => shape && frontShapes.includes(shape.id))}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Back Section */}
            {backShapes.length > 0 && (
              <div className="mt-4">
                <div className="group/back w-full flex items-center justify-between">
                  <button
                    onClick={() => handleSectionToggle("back")}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <DropdownChevronIcon
                      style={{
                        transform: sectionVisibility.back ? "rotate(0deg)" : "rotate(-90deg)",
                        transition: "transform 0.2s ease",
                      }}
                    />
                    <OrientationIcon />
                    <p
                      style={{
                        color: "#0C172C",
                        fontFeatureSettings: "'liga' off, 'clig' off",
                        fontFamily: "Inter",
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: "16px",
                        margin: 0,
                      }}
                    >
                      Back
                    </p>
                  </button>
                  <div className="flex items-center invisible group-hover/back:visible">
                    <button
                      className="shape-visibility-toggle"
                      onClick={(event) => handleToggleSectionLock(event, backShapes)}
                    >
                      {isSectionAllLocked(backShapes) ? <LockLockedIcon /> : <LockUnlockedIcon />}
                    </button>
                    <button
                      className="shape-visibility-toggle"
                      onClick={(event) => handleToggleSectionShapesVisibility(event, backShapes)}
                    >
                      {isSectionAllHidden(backShapes) ? <EyeClosedIcon16 /> : <EyeOpenIcon16 />}
                    </button>
                  </div>
                </div>
                {sectionVisibility.back && (
                  <div style={{ marginTop: 8 }}>
                    <ShapeList
                      shapeIds={backShapes}
                      depth={0}
                      shapes={shapes.filter((shape) => shape && backShapes.includes(shape.id))}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Outside Frames Section */}
            {outsideShapes.length > 0 && (
              <div className="mt-4">
                <button
                  onClick={() => handleSectionToggle("outside")}
                  className="w-full flex items-center gap-2 cursor-pointer"
                >
                  <DropdownChevronIcon
                    style={{
                      transform: sectionVisibility.outside ? "rotate(0deg)" : "rotate(-90deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                  <p
                    style={{
                      color: "#0C172C",
                      fontFeatureSettings: "'liga' off, 'clig' off",
                      fontFamily: "Inter",
                      fontSize: 14,
                      fontWeight: 400,
                      lineHeight: "16px",
                      margin: 0,
                    }}
                  >
                    Outside Frames
                  </p>
                </button>
                {sectionVisibility.outside && (
                  <div style={{ marginTop: 8 }}>
                    <ShapeList
                      shapeIds={outsideShapes}
                      depth={0}
                      shapes={shapes.filter((shape) => shape && outsideShapes.includes(shape.id))}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Legacy fallback for shapes that don't fit into sections */}
            {filteredShapeIds.length > 0 &&
              frontShapes.length === 0 &&
              backShapes.length === 0 &&
              outsideShapes.length === 0 && (
                <div className="mt-4">
                  {filteredShapeIds.length > 0 && <div className="font-bold">Shapes</div>}
                  {<ShapeList shapeIds={filteredShapeIds} depth={0} shapes={shapes} />}
                </div>
              )}
          </>
        )}
      </div>
    </>
  );
};

export default React.memo(NodesList);
